import { TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';

/**
 * How a term's curation status is presented, everywhere a term is rendered.
 *
 * The rule is one decision, not a per-screen judgement call: screens that each decided for
 * themselves is exactly how the same term ended up looking approved on one page and provisional on
 * the next. Every surface asks this module and renders what it returns.
 *
 * - `Active` — unchanged. The customer's live structure.
 * - `Unapproved` / `Draft` — muted, with a lightbulb after the name. These are suggestions the
 *   operator has not accepted; showing them at full strength claims a decision nobody made.
 * - `Archived` — not displayed at all. The data stays server-side (associations and recent-use
 *   intel keep accruing) but a retired term is not part of what the customer is looking at.
 *
 * An unknown/missing status is treated as Active: a term whose status could not be resolved is
 * still real, and hiding or muting it on a lookup failure would be a worse lie than showing it
 * plainly.
 */

/** Where a term is being rendered. Muting means different things in different containers. */
export type TermRenderContext =
  /** A table row keyed by the term — the WHOLE row is muted, so the row's numbers mute with it. */
  | 'table-row'
  /** A table column headed by the term — the whole column mutes, header and cells together. */
  | 'table-column'
  /** A bare name in running text or a label. Only the name mutes. */
  | 'name'
  /** A name beside a colour swatch. Both mute, or the swatch would out-shout the muted name. */
  | 'legend'
  /** A colour in a chart/diagram with no text of its own. The colour mutes. */
  | 'chart'
  /** An option in a select, listbox, or filter chip. The name mutes; the control stays usable. */
  | 'option'
  /** A pill/badge carrying the term. The badge mutes as a unit. */
  | 'badge';

/** What a surface should do with one term. */
export interface TermStatusPresentation {
  /** Do not render this term at all. */
  hidden: boolean;
  /** Render at reduced emphasis. */
  muted: boolean;
  /** Show the "this is a suggestion" lightbulb after the name. */
  showSuggestionIcon: boolean;
  /** Opacity to apply, or undefined when full strength. */
  opacity?: number;
  /** Class names to spread onto the element this context mutes. */
  className: string;
  /** Tooltip explaining the muting, or undefined when there is nothing to explain. */
  title?: string;
}

/** The muted opacity. Half strength reads as provisional without becoming unreadable. */
export const MUTED_TERM_OPACITY = 0.5;

/**
 * The Tailwind utility a muted term carries. This repo styles with Tailwind utilities only — no
 * stylesheets — so the treatment is a class name the call site spreads into its own list, not a
 * rule in a CSS file.
 *
 * It is one class for every context by design. What differs between contexts is WHICH element
 * receives it — a whole table row, a column, a swatch and its name, a bare name — not how strongly
 * it dims. Keeping the value single means "muted" cannot come to mean two different things.
 *
 * Muting is opacity rather than a colour change so a suggested brand keeps its own colour and stays
 * identifiable in a legend or chart; going grey would collide with the neutral tone the palette
 * already uses for "no dominant brand".
 */
export const MUTED_TERM_CLASS_NAME = 'opacity-50';

/** Why a muted term is muted, in the reader's terms. */
export const SUGGESTED_TERM_HINT = 'Suggested — not yet approved for your taxonomy.';

const FULL_STRENGTH: TermStatusPresentation = {
  hidden: false,
  muted: false,
  showSuggestionIcon: false,
  className: '',
};

const HIDDEN: TermStatusPresentation = {
  hidden: true,
  muted: false,
  showSuggestionIcon: false,
  className: '',
};

/** Statuses that read as "a suggestion nobody has accepted yet". */
const SUGGESTED_STATUSES: readonly TaxonomyTermStatus[] = [TaxonomyTermStatus.Unapproved, TaxonomyTermStatus.Draft];

/**
 * Whether a term is a suggestion the customer has not accepted.
 *
 * Exported because "suggested" is a decision several surfaces now act on — the lightbulb, the muting
 * and the analytics "hide suggestions" filter — and a second definition drifting from this one would
 * mean a term shown as a suggestion in one place and counted as approved in another.
 */
export function isSuggestedTerm(status: TaxonomyTermStatus | undefined): boolean {
  return status !== undefined && SUGGESTED_STATUSES.includes(status);
}

export interface TermPresentationOptions {
  /**
   * Never mute, whatever the status.
   *
   * The recommendation/winner display is the one exception: it reports what the AI actually
   * recommended. That is a measurement about the answer, not a claim about the customer's
   * taxonomy, and muting it would misrepresent the finding rather than qualify a term.
   */
  neverMute?: boolean;
}

/**
 * The presentation for one term, given its status and where it is being rendered.
 *
 * `neverMute` suppresses muting but NOT hiding: an archived term stays hidden even in a
 * never-muted context, because hiding is about what belongs on screen at all.
 */
export function termStatusPresentation(
  status: TaxonomyTermStatus | undefined,
  context: TermRenderContext,
  options?: TermPresentationOptions
): TermStatusPresentation {
  if (status === TaxonomyTermStatus.Archived) {
    return HIDDEN;
  }

  if (!status || !SUGGESTED_STATUSES.includes(status) || options?.neverMute) {
    return FULL_STRENGTH;
  }

  return {
    hidden: false,
    muted: true,
    // A colour-only context has no name to sit after, so there is nowhere to put the icon.
    showSuggestionIcon: context !== 'chart',
    opacity: MUTED_TERM_OPACITY,
    className: MUTED_TERM_CLASS_NAME,
    title: SUGGESTED_TERM_HINT,
  };
}

/**
 * The statuses a term list may offer as choices.
 *
 * Used as the server-side filter on the terms fetch behind filter dropdowns. Excluding archived
 * terms there rather than after the fact matters for more than tidiness: archived terms are the
 * largest population by far, so leaving them in would multiply what every read transfers, for terms
 * no list offers.
 */
export const SELECTABLE_TERM_STATUSES: readonly TaxonomyTermStatus[] = [
  TaxonomyTermStatus.Active,
  TaxonomyTermStatus.Unapproved,
  TaxonomyTermStatus.Draft,
];

/**
 * The suggestion marker for a term rendered inside a native `<option>`.
 *
 * The lightbulb is markup everywhere else; a select cannot host an icon, so it has to be a
 * character. It lives here so the two representations stay one decision.
 */
export const SUGGESTED_OPTION_MARKER = '💡';

/** One choice in a filter dropdown, already carrying its presentation. */
export interface TermFilterOption {
  key: string;
  /** The label to render, including the suggestion marker when the term is a suggestion. */
  label: string;
  /** The plain name, without any marker. */
  name: string;
  presentation: TermStatusPresentation;
}

/**
 * Build the choices for a filter dropdown from a term list.
 *
 * Archived terms are not offered. The exception is a term that is *currently selected*: a saved
 * view or a deep link may reference a term that has since been archived, and silently dropping it
 * would either reset the user's filter or leave the control blank while still filtering by it.
 * Such a term is kept so the value stays honest and visible, but it is never offered to anyone who
 * does not already have it selected.
 */
export function buildTermFilterOptions(
  terms: readonly { key?: unknown; name?: unknown; status?: TaxonomyTermStatus }[],
  selectedKey?: string
): TermFilterOption[] {
  const selected = String(selectedKey ?? '').trim();
  const options: TermFilterOption[] = [];

  for (const term of terms) {
    const key = String(term?.key ?? '').trim();
    const name = String(term?.name ?? key).trim();
    if (!key || !name) {
      continue;
    }

    const presentation = termStatusPresentation(term?.status, 'option');
    if (presentation.hidden && key !== selected) {
      continue;
    }

    options.push({
      key,
      name,
      label: presentation.showSuggestionIcon ? `${name} ${SUGGESTED_OPTION_MARKER}` : name,
      presentation,
    });
  }

  return options.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Whether a term should be dropped from a collection before rendering.
 *
 * Filtering happens in the view only. The server keeps every status — associations, stats rows and
 * recent-use intel all continue to accrue for an archived term — so un-archiving one immediately
 * surfaces its full history instead of starting from nothing.
 */
export function isDisplayableTerm(status: TaxonomyTermStatus | undefined): boolean {
  return status !== TaxonomyTermStatus.Archived;
}

/**
 * Drop the archived entries from a list of term-bearing rows.
 *
 * Takes the status accessor rather than a fixed shape so run rows, stats rows and legend entries
 * can all use it without a per-surface reimplementation.
 */
export function withoutArchivedTerms<T>(items: readonly T[], statusOf: (item: T) => TaxonomyTermStatus | undefined): T[] {
  return items.filter((item) => isDisplayableTerm(statusOf(item)));
}

/**
 * Apply a status's muting to a colour used in a chart or legend swatch.
 *
 * Returns the colour unchanged at full strength, and a same-hue translucent version when muted, so
 * a muted series stays identifiable as itself rather than turning grey and colliding with the
 * neutral palette.
 */
export function mutedColorFor(color: string | undefined, presentation: TermStatusPresentation): string | undefined {
  if (!color || !presentation.muted) {
    return color;
  }

  return applyColorOpacity(color, MUTED_TERM_OPACITY);
}

/** Fold an opacity into a colour, keeping its hue. Falls back to the input when unparseable. */
function applyColorOpacity(color: string, opacity: number): string {
  const hex = color.trim();
  const shorthand = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hex);
  const full = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);

  if (shorthand) {
    const [, r, g, b] = shorthand;
    return `rgba(${parseInt(r + r, 16)}, ${parseInt(g + g, 16)}, ${parseInt(b + b, 16)}, ${opacity})`;
  }

  if (full) {
    const [, r, g, b] = full;
    return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}, ${opacity})`;
  }

  return color;
}
